import { useCallback, useMemo, useRef } from 'react';

import {
  useChangeJournalMutation,
  ChangeJournalMutationVariables,
} from 'core/graphql';
import { EditingJournalQuery, JournalQuery } from 'core/operations';
import { useParseServerErrors } from 'services/ErrorsService';
import { IEditJournalPopupValues } from 'components/molecules/EditJournalPopup';
import { ErrorsType } from 'types';
import {
  ILessonTimeDay,
  lessonTimeFormErrors,
} from 'core/hooks/Groups/lessonTimeErrors';

type UpdateJournalType = [
  (variables: ChangeJournalMutationVariables) => void,
  {
    success: boolean;
    loading: boolean;
    errors?: ErrorsType<IEditJournalPopupValues> | null;
  },
];

export const useUpdateJournalMutation = (): UpdateJournalType => {
  const [changeJournalMutation, { data, loading }] = useChangeJournalMutation({
    onError: (err) => err,
  });

  /** Дни последней отправки — чтобы подсветить «конец» у нужного (F8). */
  const sentDays = useRef<ILessonTimeDay[]>([]);
  const customErrors = data?.updateJournal?.customErrors;
  // Перепроверка F8: отказ по длительности занятия — под «концом» дня, а не
  // общим тостом разбора ошибок «Что-то пошло не так».
  const lessonTimeErrors = useMemo(
    () => lessonTimeFormErrors(customErrors, sentDays.current),
    [customErrors],
  );
  const parsedErrors = useParseServerErrors(
    lessonTimeErrors ? undefined : customErrors,
  );
  const serverErrors = lessonTimeErrors
    ? ((lessonTimeErrors as unknown) as ErrorsType<IEditJournalPopupValues>)
    : parsedErrors;

  const updateJournal = useCallback(
    (variables: ChangeJournalMutationVariables) => {
      sentDays.current = variables.daysOfClass || [];
      return changeJournalMutation({
        variables,
        refetchQueries: [
          {
            query: JournalQuery,
            variables: {
              groupId: variables.groupId.toString(),
            },
          },
          {
            query: EditingJournalQuery,
            variables: {
              groupId: variables.groupId,
            },
          },
        ],
      });
    },
    [changeJournalMutation],
  );

  return [
    updateJournal,
    {
      success: Boolean(data?.updateJournal?.success),
      loading,
      errors: serverErrors,
    },
  ];
};
